import React, { useEffect, useState, useRef } from 'react'
import { Switch, Route, useLocation } from 'react-router-dom'
import ReactGA from 'react-ga'
import AuthBG from 'assets/ps2-bg.jpg'

import { isMobile } from 'react-device-detect'

import { AuthWrapper, VodLiveStream, PageNotFound } from 'components'
import { UserAuthenticatedRoutes } from './UserAuthenticatedRoutes'
import { GameWrapper, MobileWrapper } from 'UI'
import { graphQLQuery, graphQLSubscription } from 'graphql/helpers'
import { onUpdateEventConfig } from 'graphql/subscriptions'
import { getEventConfig } from 'graphql/queries'
import { defaultEventConfigs, Environments, EventConfig, EventStages, ISubscriptionObject } from 'types'
import { useAppState } from 'providers'

export const AnalyticsWrapper = () => {
  const location = useLocation()
  const {
    appState: { user, showLivestream },
    setShowLivestream,
    setUser,
    setAppEventStage
  } = useAppState()
  const [postLiveStream, setPostLiveStream] = useState<boolean>(false)
  const [eventConfig, setEventConfig] = useState<EventConfig>(defaultEventConfigs[getEnvironment()])
  const [eventStage, setEventStage] = useState<EventStages>(EventStages.REGISTRATION)
  const [streamStartTime, setStreamStartTime] = useState<string | undefined>()
  let subscription = useRef<ISubscriptionObject | null>(null)

  function getEnvironment() {
    const domain = window.location.hostname

    if (/staging./i.test(domain) || /staging/i.test(domain)) {
      return Environments.staging
    } else if (/dev./i.test(domain) || /dev/i.test(domain)) {
      return Environments.dev
    } else if (/qa/i.test(domain)) {
      return Environments.qa
    } else if (/verizonvirtualevents/i.test(domain) || /master/i.test(domain)) {
      return Environments.prod
    } else {
      return Environments.local
    }
  }

  const getEventConfiguration = async () => {
    try {
      const eventConfigData = await graphQLQuery(getEventConfig, 'getEventConfig', { id: eventConfig.id })
      setEventConfig({ ...eventConfigData })
      setStreamStartTime(eventConfigData.streamStartTime)
      setEventStage(eventConfigData.stage)
      setAppEventStage(eventConfigData.stage)
    } catch (err) {
      console.error('Error getting event config', err)
    }
  }

  const setupEventConfigSub = async () => {
    if (
      subscription &&
      subscription?.current?.unsubscribe &&
      typeof subscription?.current?.unsubscribe === 'function'
    ) {
      subscription?.current?.unsubscribe()
    }
    subscription.current = graphQLSubscription(onUpdateEventConfig, { id: eventConfig.id }, async config => {
      await getEventConfiguration()
    })
  }
  useEffect(() => {
    ReactGA.initialize('UA-178694733-3')
  }, [])

  useEffect(() => {
    getEventConfiguration()
    setupEventConfigSub()

    return () => {
      if (subscription && subscription.current) {
        subscription?.current?.unsubscribe()
      }
    }
    // eslint-disable-next-line
  }, [])

  useEffect(() => {
    ReactGA.pageview(location.pathname)
  }, [location])

  useEffect(() => {
    if ([EventStages.LIVESTREAM, EventStages.COUNTDOWN].includes(eventStage as EventStages)) {
      if (!showLivestream) {
        setShowLivestream(true)
      }
    } else if (showLivestream) {
      setShowLivestream(false)
    }
    // eslint-disable-next-line
  }, [eventStage])

  return (
    <Switch>
      <Route exact path='/'>
        <AuthWrapper user={user} eventStage={eventStage} backgroundImage={AuthBG} setUser={setUser} />
      </Route>
      <UserAuthenticatedRoutes eventConfig={eventConfig}>
        <Switch>
          <Route exact path='/stream'>
            <VodLiveStream
              startTime={new Date(Number(eventConfig?.streamStartTime) || '')}
              eventStage={eventStage}
              setPostLiveStream={setPostLiveStream}
            />
          </Route>
          {/* Turn off exact if we're on mobile so we can use child routes */}
          <Route path='/event' exact={!isMobile}>
            {isMobile ? (
              <MobileWrapper user={user} eventStage={eventStage} />
            ) : (
              <GameWrapper
                user={user}
                eventStage={eventStage}
                streamStartTime={streamStartTime}
                postLiveStream={postLiveStream}
                setPostLiveStream={setPostLiveStream}
              />
            )}
          </Route>
          <Route path='*' component={PageNotFound} />
        </Switch>
      </UserAuthenticatedRoutes>
    </Switch>
  )
}
